import { useState, useEffect, useCallback, useMemo, useRef, ComponentType } from 'react';
import toast from 'react-hot-toast';
import {
	Cog6ToothIcon,
	ExclamationTriangleIcon,
	WrenchScrewdriverIcon,
	ClipboardDocumentListIcon,
	UserGroupIcon,
	MapPinIcon,
	PlusIcon,
	PencilIcon,
	TrashIcon,
	BuildingStorefrontIcon,
	PhoneIcon,
	EnvelopeIcon,
	MagnifyingGlassIcon,
	CheckIcon,
	ArrowUpTrayIcon,
	ArrowLeftIcon,
	CheckCircleIcon,
	XCircleIcon,
	ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Button, ConfirmDialog, Spinner } from '../../components/ui';
import { ServiceModal } from '../../components/providers';
import CsvUploadZone from '../../components/providers/CsvUploadZone';
import AccountTab from '../../components/shared/AccountTab';
import { providerProfileApi, appointmentsApi, ApiError } from '../../api';
import { useLocationModal } from '../../contexts';
import useProviderProfileStore from '../../stores/providerProfileStore';
import useServicesStore from '../../stores/servicesStore';
import type { Service } from '../../stores/servicesStore';
import { validateServiceCsvFile, downloadCsvString } from '../../utils/csvParser';
import type { ProviderLocation, ProviderService, TaskMethod, BulkUploadResponse, BulkUploadStatus } from '../../types/provider';
import type { ServiceTask } from '../../types/appointments';

// Tab definitions
type TabId = 'services' | 'tasks' | 'roles' | 'locations' | 'account';

interface Tab {
	id: TabId;
	label: string;
	icon: ComponentType<{ className?: string }>;
}

const TABS: Tab[] = [
	{ id: 'services', label: 'Services', icon: WrenchScrewdriverIcon },
	{ id: 'tasks', label: 'Tasks', icon: ClipboardDocumentListIcon },
	{ id: 'roles', label: 'Roles & Permissions', icon: UserGroupIcon },
	{ id: 'locations', label: 'Location Details', icon: MapPinIcon },
	{ id: 'account', label: 'Account', icon: Cog6ToothIcon },
];

// Coming Soon placeholder component
const ComingSoonPlaceholder = ({ tabLabel }: { tabLabel: string }) => (
	<div className="settings-card">
		<div className="py-16 text-center">
			<WrenchScrewdriverIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
			<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
				{tabLabel}
			</h3>
			<p className="text-gray-500 dark:text-gray-400">This section is coming soon.</p>
		</div>
	</div>
);

// Services Tab - Manage salon services and pricing
type ServiceView = 'list' | 'method-chooser' | 'csv-upload';
type CsvStatusType = 'info' | 'success' | 'error' | 'warning' | null;

const ServicesTab = () => {
	const { services, isLoading, error, fetchServices, addService, updateService, deleteService } =
		useServicesStore();
	const { selectedLocationId } = useProviderProfileStore();
	const [searchTerm, setSearchTerm] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingService, setEditingService] = useState<Service | null>(null);
	const [deletingService, setDeletingService] = useState<Service | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	// CSV Upload state
	const [currentView, setCurrentView] = useState<ServiceView>('list');
	const [csvStatusMessage, setCsvStatusMessage] = useState<string | null>(null);
	const [csvStatusType, setCsvStatusType] = useState<CsvStatusType>(null);
	const [csvFile, setCsvFile] = useState<File | null>(null);
	const [csvFileInfo, setCsvFileInfo] = useState<{ serviceCount: number; totalRows: number } | null>(null);
	const [isBulkUploading, setIsBulkUploading] = useState(false);
	const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

	// Polling state
	const [uploadStatus, setUploadStatus] = useState<BulkUploadStatus | null>(null);
	const [uploadResult, setUploadResult] = useState<BulkUploadResponse | null>(null);
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// Fetch services when selected location changes
	useEffect(() => {
		if (selectedLocationId) {
			fetchServices(selectedLocationId);
		}
	}, [selectedLocationId, fetchServices]);

	// Clean up polling on unmount
	useEffect(() => {
		return () => {
			if (pollingRef.current) clearInterval(pollingRef.current);
		};
	}, []);

	const stopPolling = useCallback(() => {
		if (pollingRef.current) {
			clearInterval(pollingRef.current);
			pollingRef.current = null;
		}
	}, []);

	const startPolling = useCallback(
		(jobId: number) => {
			stopPolling();
			setUploadStatus('PENDING');
			setUploadResult(null);

			const POLL_INTERVAL = 3000; // 3 seconds

			pollingRef.current = setInterval(async () => {
				try {
					const result = await providerProfileApi.getBulkUploadStatus(jobId);
					setUploadStatus(result.status);
					setUploadResult(result);

					const terminalStatuses: BulkUploadStatus[] = ['COMPLETED', 'FAILED', 'PARTIALLY_COMPLETED'];
					if (terminalStatuses.includes(result.status)) {
						stopPolling();

						if (result.status === 'COMPLETED') {
							toast.success(`All ${result.successRows} row(s) uploaded successfully!`);
						} else if (result.status === 'PARTIALLY_COMPLETED') {
							toast.success(
								`${result.successRows} row(s) uploaded. ${result.failedRows} row(s) failed.`
							);
						} else {
							toast.error('Bulk upload failed. Please check the errors and try again.');
						}

						// Refresh services list after terminal status
						if (selectedLocationId) {
							await fetchServices(selectedLocationId);
						}
					}
				} catch {
					stopPolling();
					setUploadStatus('FAILED');
					toast.error('Failed to check upload status');
				}
			}, POLL_INTERVAL);
		},
		[selectedLocationId, fetchServices, stopPolling]
	);

	const filteredServices = services.filter(
		service =>
			service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			service.description.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const openAddModal = () => {
		setEditingService(null);
		setCurrentView('method-chooser');
	};

	const handleSelectManual = () => {
		setCurrentView('list');
		setEditingService(null);
		setIsModalOpen(true);
	};

	const handleSelectCsv = () => {
		setCurrentView('csv-upload');
		setCsvStatusMessage(null);
		setCsvStatusType(null);
	};

	const handleBackToList = () => {
		setCurrentView('list');
		setCsvStatusMessage(null);
		setCsvStatusType(null);
		setCsvFile(null);
		setCsvFileInfo(null);
	};

	const handleDownloadTemplate = useCallback(async () => {
		if (!selectedLocationId) return;
		setIsDownloadingTemplate(true);
		try {
			const bytes = await providerProfileApi.downloadServiceTemplate(selectedLocationId);
			downloadCsvString(bytes, 'service_template.csv');
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to download template');
		} finally {
			setIsDownloadingTemplate(false);
		}
	}, [selectedLocationId]);

	const handleCsvFile = useCallback(
		async (file: File) => {
			setCsvStatusMessage('Validating CSV...');
			setCsvStatusType('info');

			const result = await validateServiceCsvFile(file);

			if (!result.valid) {
				setCsvStatusMessage(result.error || 'Invalid CSV file');
				setCsvStatusType('error');
				setCsvFile(null);
				setCsvFileInfo(null);
				return;
			}

			setCsvFile(file);
			setCsvFileInfo({ serviceCount: result.serviceCount, totalRows: result.totalRows });
			setCsvStatusMessage(
				`Found ${result.serviceCount} service(s) with ${result.totalRows} row(s)`
			);
			setCsvStatusType('success');
		},
		[]
	);

	const handleBulkUpload = async () => {
		if (!selectedLocationId || !csvFile) return;

		setIsBulkUploading(true);
		try {
			const result = await providerProfileApi.bulkUploadServices(selectedLocationId, csvFile);

			// File submitted — clear file state
			setCsvFile(null);
			setCsvFileInfo(null);

			// Start polling with the job ID from the response
			toast.success('File uploaded! Processing your services...');
			startPolling(result.id);
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Bulk upload failed');
		} finally {
			setIsBulkUploading(false);
		}
	};

	const handleDismissStatus = () => {
		setUploadStatus(null);
		setUploadResult(null);
		setCurrentView('list');
	};

	const openEditModal = (service: Service) => {
		setEditingService(service);
		setIsModalOpen(true);
	};

	const handleSave = async (service: Service) => {
		if (!selectedLocationId) return;

		try {
			if (editingService) {
				await updateService(selectedLocationId, editingService.id, service);
				toast.success('Service updated successfully!');
			} else {
				await addService(selectedLocationId, service);
				toast.success('Service added successfully!');
			}
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to save service');
			throw err;
		}
	};

	const handleDeleteFromCard = (service: Service) => {
		setDeletingService(service);
	};

	const confirmDelete = async () => {
		if (!deletingService || !selectedLocationId) return;

		setIsDeleting(true);
		try {
			await deleteService(selectedLocationId, deletingService.id);
			toast.success('Service deleted successfully!');
			setDeletingService(null);
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to delete service');
		} finally {
			setIsDeleting(false);
		}
	};

	const headerTitle =
		currentView === 'list'
			? 'Your Services'
			: 'Add New Service';
	const headerSubtitle =
		currentView === 'list'
			? 'Manage your salon services and pricing'
			: currentView === 'method-chooser'
				? "Choose how you'd like to add your service"
				: 'Upload a CSV file with service details';

	return (
		<>
			<div className="settings-card">
				<div className="settings-card__header">
					<div className="settings-card__icon">
						<WrenchScrewdriverIcon className="w-6 h-6" />
					</div>
					<div className="flex-1">
						<h2 className="settings-card__title">{headerTitle}</h2>
						<p className="settings-card__subtitle">{headerSubtitle}</p>
					</div>
					{currentView === 'list' ? (
						<Button
							variant="primary"
							size="sm"
							onClick={openAddModal}
							className="hidden sm:flex"
						>
							<PlusIcon className="w-4 h-4 mr-1" />
							Add New Service
						</Button>
					) : (
						<Button
							variant="secondary"
							size="sm"
							onClick={handleBackToList}
							className="hidden sm:flex"
						>
							<ArrowLeftIcon className="w-4 h-4 mr-1" />
							Back to List
						</Button>
					)}
				</div>

				<div className="settings-card__body">
					{/* ─── List View ─── */}
					{currentView === 'list' && (
						<>
							{/* Mobile Add Button */}
							<Button
								variant="primary"
								onClick={openAddModal}
								className="w-full mb-4 sm:hidden"
							>
								<PlusIcon className="w-4 h-4 mr-1" />
								Add New Service
							</Button>

							{/* Search Bar */}
							{!isLoading && !error && services.length > 0 && (
								<div className="service-search">
									<MagnifyingGlassIcon className="service-search__icon" />
									<input
										type="text"
										className="service-search__input"
										placeholder="Search services..."
										value={searchTerm}
										onChange={e => setSearchTerm(e.target.value)}
									/>
								</div>
							)}

							{/* Loading State */}
							{isLoading ? (
								<div className="py-12 text-center">
									<Spinner
										size="lg"
										className="mx-auto mb-3 text-purple-600 dark:text-purple-400"
									/>
									<p className="text-sm text-gray-500 dark:text-gray-400">
										Loading services...
									</p>
								</div>
							) : error ? (
								<div className="py-12 text-center">
									<ExclamationTriangleIcon className="w-16 h-16 mx-auto text-red-300 dark:text-red-600 mb-4" />
									<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
										Failed to load services
									</h3>
									<p className="text-gray-500 dark:text-gray-400 mb-4">
										{error}
									</p>
									{selectedLocationId && (
										<Button
											variant="secondary"
											onClick={() =>
												fetchServices(selectedLocationId)
											}
										>
											Try Again
										</Button>
									)}
								</div>
							) : filteredServices.length === 0 ? (
								<div className="py-12 text-center">
									<WrenchScrewdriverIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
									<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
										{searchTerm
											? 'No services found'
											: 'No Services Yet'}
									</h3>
									<p className="text-gray-500 dark:text-gray-400 mb-4">
										{searchTerm
											? 'Try a different search term'
											: 'Get started by adding your first service.'}
									</p>
									{!searchTerm && (
										<Button
											variant="primary"
											onClick={openAddModal}
										>
											<PlusIcon className="w-4 h-4 mr-1" />
											Add First Service
										</Button>
									)}
								</div>
							) : (
								<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
									{filteredServices.map(service => (
										<div
											key={service.id}
											className="service-card"
										>
											<div className="service-card__body">
												<h3 className="service-card__name">
													{service.name}
												</h3>
												{service.description && (
													<p className="service-card__description">
														{service.description}
													</p>
												)}
												<div className="service-card__meta">
													{service.variants.length}{' '}
													combination
													{service.variants.length !== 1
														? 's'
														: ''}
												</div>
											</div>
											<div className="service-card__footer">
												<button
													onClick={() =>
														openEditModal(service)
													}
													className="service-card__action service-card__action--edit"
													title="Edit service"
												>
													<PencilIcon className="w-4 h-4" />
													<span>Edit</span>
												</button>
												<button
													onClick={() =>
														handleDeleteFromCard(
															service
														)
													}
													className="service-card__action service-card__action--delete"
													title="Delete service"
												>
													<TrashIcon className="w-4 h-4" />
												</button>
											</div>
										</div>
									))}
								</div>
							)}
						</>
					)}

					{/* ─── Method Chooser View ─── */}
					{currentView === 'method-chooser' && (
						<>
							{/* Mobile Back Button */}
							<Button
								variant="secondary"
								onClick={handleBackToList}
								className="w-full mb-4 sm:hidden"
							>
								<ArrowLeftIcon className="w-4 h-4 mr-1" />
								Back to List
							</Button>

							<div className="method-chooser">
								<div className="method-chooser__grid">
									<button
										className="method-chooser__card"
										onClick={handleSelectManual}
									>
										<PencilIcon className="method-chooser__card-icon" />
										<h3 className="method-chooser__card-title">
											Manual Entry
										</h3>
										<p className="method-chooser__card-desc">
											Add service details and pricing combinations one
											by one
										</p>
										<span className="method-chooser__card-hint">
											Best for single services
										</span>
									</button>

									<button
										className="method-chooser__card"
										onClick={handleSelectCsv}
									>
										<ArrowUpTrayIcon className="method-chooser__card-icon" />
										<h3 className="method-chooser__card-title">
											CSV Upload
										</h3>
										<p className="method-chooser__card-desc">
											Import multiple services with all pricing at once
											via spreadsheet
										</p>
										<span className="method-chooser__card-hint">
											Best for bulk import
										</span>
									</button>
								</div>
							</div>
						</>
					)}

					{/* ─── CSV Upload View ─── */}
					{currentView === 'csv-upload' && (
						<>
							{/* Mobile Back Button */}
							{!uploadStatus && (
								<Button
									variant="secondary"
									onClick={handleBackToList}
									className="w-full mb-4 sm:hidden"
								>
									<ArrowLeftIcon className="w-4 h-4 mr-1" />
									Back to List
								</Button>
							)}

							{/* Processing / Result Status */}
							{uploadStatus ? (
								<div className="bulk-upload-status">
									{/* In-progress: PENDING / PROCESSING */}
									{(uploadStatus === 'PENDING' || uploadStatus === 'PROCESSING') && (
										<div className="bulk-upload-status__card bulk-upload-status__card--processing">
											<div className="bulk-upload-status__spinner" />
											<h3 className="bulk-upload-status__title">
												{uploadStatus === 'PENDING' ? 'Upload Queued' : 'Processing Services'}
											</h3>
											<p className="bulk-upload-status__message">
												{uploadStatus === 'PENDING'
													? 'Your file has been submitted and is waiting to be processed...'
													: 'Your services are being created. This may take a moment...'}
											</p>
											{uploadResult && uploadResult.processedRows > 0 && (
												<p className="bulk-upload-status__progress">
													{uploadResult.processedRows} of {uploadResult.totalRows} rows processed
												</p>
											)}
										</div>
									)}

									{/* Terminal: COMPLETED */}
									{uploadStatus === 'COMPLETED' && uploadResult && (
										<div className="bulk-upload-status__card bulk-upload-status__card--success">
											<CheckCircleIcon className="bulk-upload-status__icon bulk-upload-status__icon--success" />
											<h3 className="bulk-upload-status__title">Upload Complete</h3>
											<p className="bulk-upload-status__message">
												All {uploadResult.successRows} row(s) were uploaded successfully!
											</p>
											<Button variant="primary" onClick={handleDismissStatus} className="mt-4">
												View Services
											</Button>
										</div>
									)}

									{/* Terminal: PARTIALLY_COMPLETED */}
									{uploadStatus === 'PARTIALLY_COMPLETED' && uploadResult && (
										<div className="bulk-upload-status__card bulk-upload-status__card--warning">
											<ExclamationCircleIcon className="bulk-upload-status__icon bulk-upload-status__icon--warning" />
											<h3 className="bulk-upload-status__title">Partially Completed</h3>
											<p className="bulk-upload-status__message">
												{uploadResult.successRows} row(s) uploaded successfully.{' '}
												{uploadResult.failedRows} row(s) failed.
											</p>
											{uploadResult.errors.length > 0 && (
												<div className="bulk-upload-status__errors">
													<h4>Errors:</h4>
													<ul>
														{uploadResult.errors.map((err, i) => (
															<li key={i}>Row {err.rowIndex}: {err.errorMessage}</li>
														))}
													</ul>
												</div>
											)}
											<Button variant="primary" onClick={handleDismissStatus} className="mt-4">
												View Services
											</Button>
										</div>
									)}

									{/* Terminal: FAILED */}
									{uploadStatus === 'FAILED' && (
										<div className="bulk-upload-status__card bulk-upload-status__card--error">
											<XCircleIcon className="bulk-upload-status__icon bulk-upload-status__icon--error" />
											<h3 className="bulk-upload-status__title">Upload Failed</h3>
											<p className="bulk-upload-status__message">
												The bulk upload could not be completed. Please check your CSV file and try again.
											</p>
											{uploadResult && uploadResult.errors.length > 0 && (
												<div className="bulk-upload-status__errors">
													<h4>Errors:</h4>
													<ul>
														{uploadResult.errors.map((err, i) => (
															<li key={i}>Row {err.rowIndex}: {err.errorMessage}</li>
														))}
													</ul>
												</div>
											)}
											<Button variant="secondary" onClick={handleDismissStatus} className="mt-4">
												Back to Services
											</Button>
										</div>
									)}
								</div>
							) : (
								<>
									<CsvUploadZone
										label="CSV Upload"
										description="Bulk-upload service pricing. Each row includes the service name so you can import multiple services at once."
										onFile={handleCsvFile}
										onDownloadTemplate={handleDownloadTemplate}
										isDownloading={isDownloadingTemplate}
										statusMessage={csvStatusMessage}
										statusType={csvStatusType}
										formatGuideItems={[
											'Required columns: serviceId, serviceName, size, length, durationMinutes, price',
											'Optional columns: description, status',
											'Download the template above to see the correct format',
											'You can include multiple services in one CSV file',
										]}
									/>

									{/* File Info + Upload Button */}
									{csvFile && csvFileInfo && (
										<div className="csv-upload__file-info">
											<div className="csv-upload__file-info-details">
												<p className="csv-upload__file-info-name">{csvFile.name}</p>
												<p className="csv-upload__file-info-meta">
													{csvFileInfo.serviceCount} service(s) &middot; {csvFileInfo.totalRows} row(s)
												</p>
											</div>
											<Button
												variant="primary"
												onClick={handleBulkUpload}
												isLoading={isBulkUploading}
											>
												<ArrowUpTrayIcon className="w-4 h-4 mr-1" />
												Upload Services
											</Button>
										</div>
									)}

									<div className="mt-6">
										<Button
											variant="secondary"
											onClick={handleSelectManual}
										>
											<PencilIcon className="w-4 h-4 mr-1" />
											Switch to Manual Entry
										</Button>
									</div>
								</>
							)}
						</>
					)}
				</div>
			</div>

			<ServiceModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				service={editingService}
				onSave={handleSave}
				onDelete={(id: string) => {
					setIsModalOpen(false);
					const svc = services.find(s => s.id === id);
					if (svc) setDeletingService(svc);
				}}
			/>

			<ConfirmDialog
				isOpen={!!deletingService}
				onClose={() => setDeletingService(null)}
				onConfirm={confirmDelete}
				title="Delete Service"
				message={`Are you sure you want to delete "${deletingService?.name}" and all its pricing combinations? This action cannot be undone.`}
				confirmLabel="Yes, Delete"
				variant="danger"
				isLoading={isDeleting}
			/>
		</>
	);
};

// Task type formatting helpers
const formatTaskType = (type: string) => {
	switch (type) {
		case 'WHOLE': return 'Whole';
		case 'FRONT': return 'Front';
		case 'BACK': return 'Back';
		default: return type.charAt(0) + type.slice(1).toLowerCase();
	}
};

const formatSize = (size: string) => {
	const sizeMap: Record<string, string> = {
		XXS: 'XXS', XS: 'XS', S: 'Small', SM: 'SM', M: 'Medium',
		L: 'Large', XL: 'XL', JUMBO: 'Jumbo',
	};
	return sizeMap[size] || size;
};

const formatLength = (length: string) => {
	return length
		.split('_')
		.map(w => w.charAt(0) + w.slice(1).toLowerCase())
		.join(' ');
};

const TASK_TYPE_COLORS: Record<string, string> = {
	WHOLE: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
	FRONT: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
	BACK: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};

// Dummy itemized tasks for display (API in progress)
const DUMMY_ITEMIZED_TASKS: ServiceTask[] = [
	{
		id: 9001,
		serviceId: 100,
		serviceName: 'Box Braids',
		method: 'ITEMIZED',
		taskPricing: [
			{ serviceTaskId: 9001, taskType: 'LINER', size: 'M', length: 'MID_BACK', durationMinutes: 30, price: 25 },
			{ serviceTaskId: 9001, taskType: 'LINER', size: 'L', length: 'WAIST', durationMinutes: 40, price: 35 },
		],
		status: 'ACTIVE',
		createdAt: '2026-02-10T10:00:00Z',
	},
	{
		id: 9002,
		serviceId: 100,
		serviceName: 'Box Braids',
		method: 'ITEMIZED',
		taskPricing: [
			{ serviceTaskId: 9002, taskType: 'PICKER', size: 'M', length: 'MID_BACK', durationMinutes: 45, price: 30 },
			{ serviceTaskId: 9002, taskType: 'PICKER', size: 'L', length: 'WAIST', durationMinutes: 55, price: 40 },
		],
		status: 'ACTIVE',
		createdAt: '2026-02-10T10:00:00Z',
	},
	{
		id: 9003,
		serviceId: 101,
		serviceName: 'Cornrows',
		method: 'ITEMIZED',
		taskPricing: [
			{ serviceTaskId: 9003, taskType: 'DROPPER', size: 'S', length: 'SHOULDER', durationMinutes: 20, price: 15 },
		],
		status: 'ACTIVE',
		createdAt: '2026-02-11T10:00:00Z',
	},
];

// Itemized task name options
const ITEMIZED_TASK_NAMES = [
	'Liner', 'Picker', 'Dropper', 'Feeder', 'Prepper', 'Front', 'Back',
];

// Editable pricing row type for Itemized form
interface ItemizedPricingRow {
	size: string;
	length: string;
	durationMinutes: number;
	price: number;
}

// Tasks Tab - Manage service task assignments
const TasksTab = () => {
	const { selectedLocationId } = useProviderProfileStore();
	const [serviceTasks, setServiceTasks] = useState<ServiceTask[]>([]);
	const [availableServices, setAvailableServices] = useState<ProviderService[]>([]);
	const [selectedMethod, setSelectedMethod] = useState<TaskMethod | null>(null);
	const [selectedServiceIds, setSelectedServiceIds] = useState<Set<number>>(new Set());
	const [searchTerm, setSearchTerm] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [deletingTask, setDeletingTask] = useState<ServiceTask | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	// Itemized-specific state
	const [itemizedTaskName, setItemizedTaskName] = useState('');
	const [customTaskName, setCustomTaskName] = useState('');
	const [itemizedPricing, setItemizedPricing] = useState<ItemizedPricingRow[]>([]);

	const resetForm = () => {
		setSelectedMethod(null);
		setSelectedServiceIds(new Set());
		setSearchTerm('');
		setItemizedTaskName('');
		setCustomTaskName('');
		setItemizedPricing([]);
	};

	const fetchData = useCallback(async () => {
		if (!selectedLocationId) return;
		setIsLoading(true);
		try {
			const [tasks, available] = await Promise.all([
				appointmentsApi.getServiceTasks(selectedLocationId),
				appointmentsApi.getAvailableServices(selectedLocationId),
			]);
			setServiceTasks(tasks);
			setAvailableServices(available);
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to load tasks data');
		} finally {
			setIsLoading(false);
		}
	}, [selectedLocationId]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const filteredServices = useMemo(
		() =>
			availableServices.filter(s =>
				s.serviceName.toLowerCase().includes(searchTerm.toLowerCase())
			),
		[availableServices, searchTerm]
	);

	const originalTasks = useMemo(
		() => serviceTasks.filter(t => t.method === 'ORIGINAL'),
		[serviceTasks]
	);

	// Group original tasks by serviceId
	const originalTasksByService = useMemo(() => {
		const grouped = new Map<number, ServiceTask[]>();
		originalTasks.forEach(task => {
			const existing = grouped.get(task.serviceId) || [];
			existing.push(task);
			grouped.set(task.serviceId, existing);
		});
		return grouped;
	}, [originalTasks]);

	// Combine real itemized tasks with dummy data
	const itemizedTasks = useMemo(
		() => {
			const real = serviceTasks.filter(t => t.method === 'ITEMIZED');
			return real.length > 0 ? real : DUMMY_ITEMIZED_TASKS;
		},
		[serviceTasks]
	);

	// Selected services for pricing preview (Original = multiple, Itemized = single)
	const selectedServices = useMemo(
		() => availableServices.filter(s => selectedServiceIds.has(s.id)),
		[availableServices, selectedServiceIds]
	);

	// For Itemized: the single selected service
	const selectedItemizedService = selectedMethod === 'ITEMIZED' && selectedServices.length === 1
		? selectedServices[0]
		: null;

	// Initialize itemized pricing when a service is selected for Itemized
	useEffect(() => {
		if (selectedItemizedService) {
			setItemizedPricing(
				selectedItemizedService.pricingCombinations.map(combo => ({
					size: combo.size,
					length: combo.length,
					durationMinutes: combo.durationMinutes,
					price: combo.price,
				}))
			);
		} else if (selectedMethod === 'ITEMIZED') {
			setItemizedPricing([]);
		}
	}, [selectedItemizedService, selectedMethod]);

	const toggleService = (serviceId: number) => {
		if (selectedMethod === 'ITEMIZED') {
			// Single select for Itemized: toggle or replace
			setSelectedServiceIds(prev => {
				if (prev.has(serviceId)) {
					return new Set();
				}
				return new Set([serviceId]);
			});
			setItemizedTaskName('');
			setCustomTaskName('');
		} else {
			// Multi-select for Original
			setSelectedServiceIds(prev => {
				const next = new Set(prev);
				if (next.has(serviceId)) {
					next.delete(serviceId);
				} else {
					next.add(serviceId);
				}
				return next;
			});
		}
	};

	const handleSelectAll = () => {
		if (selectedServiceIds.size === filteredServices.length) {
			setSelectedServiceIds(new Set());
		} else {
			setSelectedServiceIds(new Set(filteredServices.map(s => s.id)));
		}
	};

	const updateItemizedRow = (index: number, field: 'durationMinutes' | 'price', value: number) => {
		setItemizedPricing(prev => {
			const next = [...prev];
			next[index] = { ...next[index], [field]: value };
			return next;
		});
	};

	const getResolvedTaskName = () => {
		if (itemizedTaskName === 'custom') return customTaskName.trim();
		return itemizedTaskName;
	};

	const handleSubmit = async () => {
		if (!selectedLocationId || !selectedMethod || selectedServiceIds.size === 0) return;

		if (selectedMethod === 'ITEMIZED') {
			const taskName = getResolvedTaskName();
			if (!taskName) {
				toast.error('Please select a task name');
				return;
			}
			const validCombinations = itemizedPricing.filter(r => r.durationMinutes > 0 && r.price > 0);
			if (validCombinations.length === 0) {
				toast.error('Please enter duration and price for at least one combination');
				return;
			}
			// Itemized API in progress — show info toast
			toast.success('Itemized task saved locally (API coming soon)');
			resetForm();
			return;
		}

		// Original method
		setIsSubmitting(true);
		try {
			await appointmentsApi.createServiceTasks(selectedLocationId, {
				method: selectedMethod,
				serviceIds: Array.from(selectedServiceIds),
			});
			toast.success('Tasks generated successfully!');
			resetForm();
			await fetchData();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to generate tasks');
		} finally {
			setIsSubmitting(false);
		}
	};

	const confirmDelete = async () => {
		if (!deletingTask || !selectedLocationId) return;

		setIsDeleting(true);
		try {
			await appointmentsApi.deleteServiceTask(deletingTask.id, selectedLocationId);
			toast.success('Task deleted successfully!');
			setDeletingTask(null);
			await fetchData();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to delete task');
		} finally {
			setIsDeleting(false);
		}
	};

	// Determine if submit is enabled
	const isSubmitDisabled = useMemo(() => {
		if (selectedServiceIds.size === 0 || isSubmitting) return true;
		if (selectedMethod === 'ITEMIZED') {
			const taskName = getResolvedTaskName();
			if (!taskName) return true;
			const hasValidCombo = itemizedPricing.some(r => r.durationMinutes > 0 && r.price > 0);
			if (!hasValidCombo) return true;
		}
		return false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedServiceIds, isSubmitting, selectedMethod, itemizedTaskName, customTaskName, itemizedPricing]);

	if (isLoading) {
		return (
			<div className="settings-card">
				<div className="py-12 text-center">
					<Spinner size="lg" className="mx-auto mb-3 text-purple-600 dark:text-purple-400" />
					<p className="text-sm text-gray-500 dark:text-gray-400">Loading tasks...</p>
				</div>
			</div>
		);
	}

	return (
		<>
			{/* Add Tasks Card */}
			<div className="settings-card">
				<div className="settings-card__header">
					<div className="settings-card__icon">
						<PlusIcon className="w-6 h-6" />
					</div>
					<div className="flex-1">
						<h2 className="settings-card__title">Add Tasks</h2>
						<p className="settings-card__subtitle">
							Map services to task methods for commission calculations
						</p>
					</div>
				</div>

				<div className="settings-card__body">
					{/* Method Selection */}
					<div className="mb-6">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
							Task Assignment Method
						</label>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
							{/* Original Method Card */}
							<button
								type="button"
								onClick={() => {
									setSelectedMethod('ORIGINAL');
									setSelectedServiceIds(new Set());
									setItemizedTaskName('');
									setCustomTaskName('');
									setItemizedPricing([]);
								}}
								className={`relative p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
									selectedMethod === 'ORIGINAL'
										? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-400'
										: 'border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-600'
								}`}
							>
								{selectedMethod === 'ORIGINAL' && (
									<div className="absolute top-3 right-3">
										<CheckIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
									</div>
								)}
								<div className="flex items-center gap-2 mb-2">
									<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
										Original
									</span>
								</div>
								<p className="text-sm text-gray-600 dark:text-gray-400">
									One staff handles the WHOLE task. Auto-generates Whole, Front & Back pricing.
								</p>
							</button>

							{/* Itemized Method Card */}
							<button
								type="button"
								onClick={() => {
									setSelectedMethod('ITEMIZED');
									setSelectedServiceIds(new Set());
									setItemizedTaskName('');
									setCustomTaskName('');
									setItemizedPricing([]);
								}}
								className={`relative p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
									selectedMethod === 'ITEMIZED'
										? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-400'
										: 'border-gray-200 dark:border-gray-600 hover:border-emerald-300 dark:hover:border-emerald-600'
								}`}
							>
								{selectedMethod === 'ITEMIZED' && (
									<div className="absolute top-3 right-3">
										<CheckIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
									</div>
								)}
								<div className="flex items-center gap-2 mb-2">
									<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
										Itemized
									</span>
								</div>
								<p className="text-sm text-gray-600 dark:text-gray-400">
									Split tasks among multiple staff. Custom task assignments per service.
								</p>
							</button>
						</div>
					</div>

					{/* Service Selection */}
					{selectedMethod && (
						<div className="mb-6">
							<div className="flex items-center justify-between mb-3">
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
									{selectedMethod === 'ITEMIZED' ? 'Select Service' : 'Select Services'}
									<span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
										({selectedServiceIds.size} selected{selectedMethod === 'ITEMIZED' ? ' — select one' : ''})
									</span>
								</label>
								{selectedMethod === 'ORIGINAL' && filteredServices.length > 0 && (
									<button
										type="button"
										onClick={handleSelectAll}
										className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium cursor-pointer"
									>
										{selectedServiceIds.size === filteredServices.length
											? 'Clear All'
											: 'Select All'}
									</button>
								)}
							</div>

							{/* Search */}
							{availableServices.length > 0 && (
								<div className="relative mb-3">
									<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
									<input
										type="text"
										className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
										placeholder="Search services..."
										value={searchTerm}
										onChange={e => setSearchTerm(e.target.value)}
									/>
								</div>
							)}

							{/* Services Checklist */}
							{availableServices.length === 0 ? (
								<div className="py-8 text-center border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
									<ClipboardDocumentListIcon className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
									<p className="text-sm text-gray-500 dark:text-gray-400">
										All services have been mapped to tasks already.
									</p>
								</div>
							) : filteredServices.length === 0 ? (
								<div className="py-6 text-center border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
									<p className="text-sm text-gray-500 dark:text-gray-400">
										No services match your search.
									</p>
								</div>
							) : (
								<div className="border border-gray-200 dark:border-gray-600 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
									{filteredServices.map(service => (
										<label
											key={service.id}
											className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
										>
											<input
												type={selectedMethod === 'ITEMIZED' ? 'radio' : 'checkbox'}
												name={selectedMethod === 'ITEMIZED' ? 'itemizedService' : undefined}
												checked={selectedServiceIds.has(service.id)}
												onChange={() => toggleService(service.id)}
												className={`w-4 h-4 border-gray-300 dark:border-gray-500 focus:ring-purple-500 dark:bg-gray-700 cursor-pointer ${
													selectedMethod === 'ITEMIZED'
														? 'text-emerald-600'
														: 'text-purple-600 rounded'
												}`}
											/>
											<div className="flex-1 min-w-0">
												<span className="text-sm font-medium text-gray-900 dark:text-gray-100">
													{service.serviceName}
												</span>
												<span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
													{service.pricingCombinations.length} combination{service.pricingCombinations.length !== 1 ? 's' : ''}
												</span>
											</div>
										</label>
									))}
								</div>
							)}
						</div>
					)}

					{/* Original: Pricing Preview */}
					{selectedMethod === 'ORIGINAL' && selectedServices.length > 0 && (
						<div className="mb-6">
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
								Pricing Preview
								<span className="ml-1 text-xs text-gray-500 dark:text-gray-400 font-normal">
									— Whole (100%), Front (50%), Back (50%)
								</span>
							</label>
							<div className="space-y-3">
								{selectedServices.map(service => (
									<div
										key={service.id}
										className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
									>
										<div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
											<h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
												{service.serviceName}
											</h4>
										</div>
										<div className="px-4 py-2 overflow-x-auto">
											<table className="w-full text-xs">
												<thead>
													<tr className="text-left text-gray-500 dark:text-gray-400">
														<th className="pb-1.5 pr-3 font-semibold">Size</th>
														<th className="pb-1.5 pr-3 font-semibold">Length</th>
														<th className="pb-1.5 pr-3 font-semibold text-purple-600 dark:text-purple-400">Whole</th>
														<th className="pb-1.5 pr-3 font-semibold text-emerald-600 dark:text-emerald-400">Front</th>
														<th className="pb-1.5 font-semibold text-amber-600 dark:text-amber-400">Back</th>
													</tr>
												</thead>
												<tbody className="text-gray-700 dark:text-gray-300">
													{service.pricingCombinations.map((combo, idx) => (
														<tr key={idx}>
															<td className="py-0.5 pr-3">{formatSize(combo.size)}</td>
															<td className="py-0.5 pr-3">{formatLength(combo.length)}</td>
															<td className="py-0.5 pr-3">
																<span className="text-gray-500 dark:text-gray-400">{combo.durationMinutes}m</span>
																{' / '}
																<span className="text-emerald-600 dark:text-emerald-400 font-medium">${combo.price.toFixed(2)}</span>
															</td>
															<td className="py-0.5 pr-3">
																<span className="text-gray-500 dark:text-gray-400">{Math.round(combo.durationMinutes / 2)}m</span>
																{' / '}
																<span className="text-emerald-600 dark:text-emerald-400 font-medium">${(combo.price / 2).toFixed(2)}</span>
															</td>
															<td className="py-0.5">
																<span className="text-gray-500 dark:text-gray-400">{Math.round(combo.durationMinutes / 2)}m</span>
																{' / '}
																<span className="text-emerald-600 dark:text-emerald-400 font-medium">${(combo.price / 2).toFixed(2)}</span>
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Itemized: Task Name + Editable Pricing */}
					{selectedMethod === 'ITEMIZED' && selectedItemizedService && (
						<div className="mb-6 space-y-4">
							{/* Selected Service Info */}
							<div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg px-4 py-3">
								<div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-0.5">Selected Service</div>
								<div className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">{selectedItemizedService.serviceName}</div>
							</div>

							{/* Task Name Dropdown */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									Task Name <span className="text-red-500">*</span>
								</label>
								<select
									value={itemizedTaskName}
									onChange={e => {
										setItemizedTaskName(e.target.value);
										if (e.target.value !== 'custom') setCustomTaskName('');
									}}
									className="w-full sm:max-w-sm px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent cursor-pointer"
								>
									<option value="">Select a task</option>
									{ITEMIZED_TASK_NAMES.map(name => (
										<option key={name} value={name}>{name}</option>
									))}
									<option value="custom">+ Enter Custom Task</option>
								</select>
								{itemizedTaskName === 'custom' && (
									<input
										type="text"
										value={customTaskName}
										onChange={e => setCustomTaskName(e.target.value)}
										placeholder="Enter custom task name"
										className="mt-2 w-full sm:max-w-sm px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
									/>
								)}
							</div>

							{/* Editable Pricing Table */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
									Price & Duration Combinations
								</label>
								<p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
									Duration and price are pre-filled from the service. Edit as needed.
								</p>
								<div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
									<div className="max-h-80 overflow-y-auto">
										<table className="w-full text-xs">
											<thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0 z-10">
												<tr className="text-left text-gray-500 dark:text-gray-400">
													<th className="px-4 py-2.5 font-semibold">Size</th>
													<th className="px-4 py-2.5 font-semibold">Length</th>
													<th className="px-4 py-2.5 font-semibold text-center w-28">Duration (min)</th>
													<th className="px-4 py-2.5 font-semibold text-center w-28">Price ($)</th>
												</tr>
											</thead>
											<tbody className="divide-y divide-gray-100 dark:divide-gray-700">
												{itemizedPricing.map((row, idx) => (
													<tr key={idx} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-750'}>
														<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{formatSize(row.size)}</td>
														<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{formatLength(row.length)}</td>
														<td className="px-4 py-2 text-center">
															<input
																type="number"
																min={0}
																step={1}
																value={row.durationMinutes || ''}
																onChange={e => updateItemizedRow(idx, 'durationMinutes', parseInt(e.target.value) || 0)}
																className="w-20 px-2 py-1 text-center text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
																placeholder="0"
															/>
														</td>
														<td className="px-4 py-2 text-center">
															<input
																type="number"
																min={0}
																step={0.01}
																value={row.price || ''}
																onChange={e => updateItemizedRow(idx, 'price', parseFloat(e.target.value) || 0)}
																className="w-20 px-2 py-1 text-center text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 font-medium focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
																placeholder="0"
															/>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Submit Button */}
					{selectedMethod && availableServices.length > 0 && (
						<Button
							variant="primary"
							onClick={handleSubmit}
							isLoading={isSubmitting}
							disabled={isSubmitDisabled}
							className="w-full sm:w-auto"
						>
							<PlusIcon className="w-4 h-4 mr-1" />
							{selectedMethod === 'ITEMIZED' ? 'Add Task' : 'Generate Tasks'}
						</Button>
					)}
				</div>
			</div>

			{/* Your Tasks Card */}
			<div className="settings-card mt-6">
				<div className="settings-card__header">
					<div className="settings-card__icon">
						<ClipboardDocumentListIcon className="w-6 h-6" />
					</div>
					<div className="flex-1">
						<h2 className="settings-card__title">Your Tasks</h2>
						<p className="settings-card__subtitle">
							Tasks are grouped by assignment method
						</p>
					</div>
				</div>

				<div className="settings-card__body space-y-8">
					{/* Original Method Section */}
					<div>
						<div className="flex items-center gap-2 mb-4">
							<span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
								Original Method
							</span>
							<span className="text-xs text-gray-500 dark:text-gray-400">
								Whole, Front & Back tasks
							</span>
						</div>

						{originalTasksByService.size === 0 ? (
							<div className="py-8 text-center border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
								<p className="text-sm text-gray-500 dark:text-gray-400">
									No Original method tasks yet. Use the form above to add Whole, Front & Back tasks.
								</p>
							</div>
						) : (
							<div className="space-y-4">
								{Array.from(originalTasksByService.entries()).map(([serviceId, tasks]) => (
									<div
										key={serviceId}
										className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
									>
										{/* Service Header */}
										<div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
											<div className="flex items-center justify-between">
												<h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
													{tasks[0].serviceName}
												</h4>
												<div className="flex items-center gap-1">
													{tasks.map(task => {
														const taskType = task.taskPricing[0]?.taskType || '';
														return (
															<span
																key={task.id}
																className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
																	TASK_TYPE_COLORS[taskType] || 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
																}`}
															>
																{formatTaskType(taskType)}
															</span>
														);
													})}
												</div>
											</div>
										</div>

										{/* Task Pricing Grids */}
										<div className="divide-y divide-gray-100 dark:divide-gray-700">
											{tasks.map(task => {
												const taskType = task.taskPricing[0]?.taskType || '';
												return (
													<div key={task.id} className="px-4 py-3">
														<div className="flex items-center justify-between mb-2">
															<span
																className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
																	TASK_TYPE_COLORS[taskType] || 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
																}`}
															>
																{formatTaskType(taskType)}
																{taskType === 'WHOLE' ? ' (100%)' : ' (50%)'}
															</span>
															<button
																onClick={() => setDeletingTask(task)}
																className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded transition-colors cursor-pointer"
																title="Delete task"
															>
																<TrashIcon className="w-4 h-4" />
															</button>
														</div>
														<div className="overflow-x-auto">
															<table className="w-full text-xs">
																<thead>
																	<tr className="text-left text-gray-500 dark:text-gray-400">
																		<th className="pb-1 pr-4 font-semibold">Size</th>
																		<th className="pb-1 pr-4 font-semibold">Length</th>
																		<th className="pb-1 pr-4 font-semibold">Duration</th>
																		<th className="pb-1 font-semibold">Price</th>
																	</tr>
																</thead>
																<tbody className="text-gray-700 dark:text-gray-300">
																	{task.taskPricing.map((tp, idx) => (
																		<tr key={idx}>
																			<td className="py-0.5 pr-4">{formatSize(tp.size)}</td>
																			<td className="py-0.5 pr-4">{formatLength(tp.length)}</td>
																			<td className="py-0.5 pr-4">{tp.durationMinutes} min</td>
																			<td className="py-0.5 text-emerald-600 dark:text-emerald-400 font-medium">${tp.price.toFixed(2)}</td>
																		</tr>
																	))}
																</tbody>
															</table>
														</div>
													</div>
												);
											})}
										</div>
									</div>
								))}
							</div>
						)}
					</div>

					{/* Itemized Method Section */}
					<div>
						<div className="flex items-center gap-2 mb-4">
							<span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
								Itemized Method
							</span>
							<span className="text-xs text-gray-500 dark:text-gray-400">
								Liner, Picker, Dropper, Feeder, etc.
							</span>
						</div>

						{itemizedTasks.length === 0 ? (
							<div className="py-8 text-center border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
								<p className="text-sm text-gray-500 dark:text-gray-400">
									No Itemized method tasks yet. Use the form above to add tasks like Liner, Picker, Dropper, etc.
								</p>
							</div>
						) : (
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
								{itemizedTasks.map(task => {
									const taskType = task.taskPricing[0]?.taskType || 'TASK';
									const isDummy = task.id >= 9000;
									return (
										<div
											key={task.id}
											className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
										>
											<div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
												<div className="flex items-center justify-between">
													<div>
														<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
															{formatTaskType(taskType)}
														</span>
														{isDummy && (
															<span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">
																Sample
															</span>
														)}
													</div>
													{!isDummy && (
														<button
															onClick={() => setDeletingTask(task)}
															className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded transition-colors cursor-pointer"
															title="Delete task"
														>
															<TrashIcon className="w-4 h-4" />
														</button>
													)}
												</div>
											</div>
											<div className="px-4 py-3">
												<p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
													{task.serviceName}
												</p>
												<div className="overflow-x-auto">
													<table className="w-full text-xs">
														<thead>
															<tr className="text-left text-gray-500 dark:text-gray-400">
																<th className="pb-1 pr-3 font-semibold">Size</th>
																<th className="pb-1 pr-3 font-semibold">Length</th>
																<th className="pb-1 pr-3 font-semibold">Dur.</th>
																<th className="pb-1 font-semibold">Price</th>
															</tr>
														</thead>
														<tbody className="text-gray-700 dark:text-gray-300">
															{task.taskPricing.map((tp, idx) => (
																<tr key={idx}>
																	<td className="py-0.5 pr-3">{formatSize(tp.size)}</td>
																	<td className="py-0.5 pr-3">{formatLength(tp.length)}</td>
																	<td className="py-0.5 pr-3">{tp.durationMinutes}m</td>
																	<td className="py-0.5 text-emerald-600 dark:text-emerald-400 font-medium">${tp.price.toFixed(2)}</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>
			</div>

			<ConfirmDialog
				isOpen={!!deletingTask}
				onClose={() => setDeletingTask(null)}
				onConfirm={confirmDelete}
				title="Delete Task"
				message={`Are you sure you want to delete the ${formatTaskType(deletingTask?.taskPricing[0]?.taskType || '')} task for "${deletingTask?.serviceName}"? This action cannot be undone.`}
				confirmLabel="Yes, Delete"
				variant="danger"
				isLoading={isDeleting}
			/>
		</>
	);
};

// Helper to format location address
const formatAddress = (location: ProviderLocation): string => {
	const parts = [location.address, location.city, location.state, location.zipCode].filter(
		Boolean
	);
	return parts.join(', ');
};

// Locations Tab - Location List with Add/Edit Modal
const LocationsTab = () => {
	const { profile, fetchProfile } = useProviderProfileStore();
	const { openAddLocation, openEditLocation } = useLocationModal();
	const [deletingLocation, setDeletingLocation] = useState<ProviderLocation | null>(null);
	const [isDeletingLocation, setIsDeletingLocation] = useState(false);

	const locations = profile?.locations || [];

	const handleEditLocation = (location: ProviderLocation) => {
		openEditLocation(location);
	};

	const handleDeleteLocation = (location: ProviderLocation) => {
		if (location.primary) {
			toast.error(
				'Cannot delete the primary location. Please set another location as primary first.'
			);
			return;
		}
		setDeletingLocation(location);
	};

	const confirmDeleteLocation = async () => {
		if (!deletingLocation) return;

		setIsDeletingLocation(true);
		try {
			await providerProfileApi.deleteLocation(deletingLocation.id);
			toast.success('Location deleted successfully!');
			fetchProfile();
		} catch (error) {
			const apiError = error as ApiError;
			toast.error(apiError.message || 'Failed to delete location');
		} finally {
			setIsDeletingLocation(false);
			setDeletingLocation(null);
		}
	};

	return (
		<>
			{/* Your Locations Card */}
			<div className="settings-card">
				<div className="settings-card__header">
					<div className="settings-card__icon">
						<MapPinIcon className="w-6 h-6" />
					</div>
					<div className="flex-1">
						<h2 className="settings-card__title">Your Locations</h2>
						<p className="settings-card__subtitle">Manage your salon locations</p>
					</div>
					<Button
						variant="primary"
						size="sm"
						onClick={openAddLocation}
						className="hidden sm:flex"
					>
						<PlusIcon className="w-4 h-4 mr-1" />
						Add Location
					</Button>
				</div>

				<div className="settings-card__body">
					{/* Mobile Add Button */}
					<Button
						variant="primary"
						onClick={openAddLocation}
						className="w-full mb-4 sm:hidden"
					>
						<PlusIcon className="w-4 h-4 mr-1" />
						Add Location
					</Button>

					{/* Locations Grid */}
					{locations.length === 0 ? (
						<div className="py-12 text-center">
							<BuildingStorefrontIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
							<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
								No Locations Yet
							</h3>
							<p className="text-gray-500 dark:text-gray-400 mb-4">
								Add your first salon location to get started.
							</p>
						</div>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{locations.map(location => (
								<div key={location.id} className="location-card">
									<div className="location-card__header">
										<div className="location-card__badges">
											{location.primary && (
												<span className="location-card__badge location-card__badge--primary">
													Primary
												</span>
											)}
											<span
												className={`location-card__badge ${
													location.active
														? 'location-card__badge--active'
														: 'location-card__badge--inactive'
												}`}
											>
												{location.active ? 'Active' : 'Inactive'}
											</span>
										</div>
										<div className="location-card__actions">
											<button
												onClick={() => handleEditLocation(location)}
												className="location-card__edit"
												title="Edit location"
											>
												<PencilIcon className="w-4 h-4" />
											</button>
											<button
												onClick={() => handleDeleteLocation(location)}
												className="location-card__delete"
												title={
													location.primary
														? 'Cannot delete primary location'
														: 'Delete location'
												}
												disabled={location.primary}
											>
												<TrashIcon className="w-4 h-4" />
											</button>
										</div>
									</div>

									<div className="location-card__address">
										<MapPinIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
										<span>{formatAddress(location)}</span>
									</div>

									{(location.phone || location.email) && (
										<div className="location-card__contact">
											{location.phone && (
												<div className="location-card__contact-item">
													<PhoneIcon className="w-4 h-4 text-gray-400" />
													<span>{location.phone}</span>
												</div>
											)}
											{location.email && (
												<div className="location-card__contact-item">
													<EnvelopeIcon className="w-4 h-4 text-gray-400" />
													<span>{location.email}</span>
												</div>
											)}
										</div>
									)}
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			<ConfirmDialog
				isOpen={!!deletingLocation}
				onClose={() => setDeletingLocation(null)}
				onConfirm={confirmDeleteLocation}
				title="Delete Location"
				message={`Are you sure you want to delete this location?\n\n${deletingLocation ? formatAddress(deletingLocation) : ''}`}
				confirmLabel="Yes, Delete"
				variant="danger"
				isLoading={isDeletingLocation}
			/>
		</>
	);
};

const Settings = () => {
	const [activeTab, setActiveTab] = useState<TabId>('services');

	const renderTabContent = () => {
		switch (activeTab) {
			case 'account':
				return <AccountTab />;
			case 'services':
				return <ServicesTab />;
			case 'tasks':
				return <TasksTab />;
			case 'roles':
				return <ComingSoonPlaceholder tabLabel="Roles & Permissions" />;
			case 'locations':
				return <LocationsTab />;
			default:
				return null;
		}
	};

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
					Settings
				</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					Manage your account and business settings
				</p>
			</div>

			{/* Settings Tabs */}
			<div className="settings-tabs">
				{TABS.map(tab => (
					<button
						key={tab.id}
						className={`settings-tab ${activeTab === tab.id ? 'settings-tab--active' : ''}`}
						onClick={() => setActiveTab(tab.id)}
					>
						<tab.icon className="w-4 h-4 md:hidden" />
						<span className="hidden md:inline">{tab.label}</span>
						<span className="md:hidden">{tab.label.split(' ')[0]}</span>
					</button>
				))}
			</div>

			{/* Tab Content */}
			<div className="settings-content">{renderTabContent()}</div>
		</AppLayout>
	);
};

export default Settings;
